import React, { useEffect, useState } from "react";
import {
  getAllPurchaseOrders,
  updatePurchaseOrderStatus,
} from "../../Services/PurchaseOrderService";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  IconButton,
  Tooltip,
} from "@mui/material";
import Swal from "sweetalert2";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Footer from "../../Footer/Footer";

const ViewPurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId") || "ORG001";
  const userId = localStorage.getItem("userId") || "sysAdmin";

  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [filteredPOs, setFilteredPOs] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // 🔹 Fetch all purchase orders
  useEffect(() => {
    const fetchPOs = async () => {
      Swal.fire({
        title: "<strong>Fetching Purchase Orders</strong>",
        html: "<i>Please wait while we fetch the data</i>",
        allowOutsideClick: false,
        width: 400,
        didOpen: () => Swal.showLoading(),
        showConfirmButton: false,
      });
      try {
        const res = await getAllPurchaseOrders(orgId);
        const pos = res.purchaseOrders.map((po: any) => ({
          ...po,
          id: po.poId,
        }));
        setPurchaseOrders(pos);
        setFilteredPOs(pos);
      } catch (err) {
        console.error("Error fetching POs:", err);
      } finally {
        Swal.close();
      }
    };
    fetchPOs();
  }, [orgId]);

  // 🔹 Search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      const lower = searchText.toLowerCase();
      const filtered = purchaseOrders.filter(
        (po) =>
          po.poNumber?.toLowerCase().includes(lower) ||
          po.vendorName?.toLowerCase().includes(lower) ||
          po.status?.toLowerCase().includes(lower)
      );
      setFilteredPOs(filtered);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, purchaseOrders]);

  const handleStatusChange = async (poId: string, status: string) => {
    const confirm = await Swal.fire({
      title: `Are you sure you want to ${status} this PO?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${status}`,
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await updatePurchaseOrderStatus(poId,  status as "approved" | "rejected" | "cancelled" | "draft", userId);
      Swal.fire("Success", res.message, "success");
     
      setPurchaseOrders((prev) =>
        prev.map((po) => (po.poId === poId ? { ...po, status } : po))
      );
      setFilteredPOs((prev) =>
        prev.map((po) => (po.poId === poId ? { ...po, status } : po))
      );
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to update status", "error");
    }
  };

  const handleView = (poId: string) => {
    window.open(`/dashboard/purchase/view-po-orders?poId=${poId}`, "_blank");
  };
  const handleEdit = (po: any) => {
  navigate("/dashboard/purchase/edit-po", {
    state: {
      poId: po.poId,
      poNumber:po.poNumber,
      orgId: po.orgId,
      financialYear: po.financialYear,
    },
  });
};

  const columns: GridColDef[] = [
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => {
      const isClosed = params.row.status === "close";
      const isCancel = params.row.status === "cancelled";
      const isReject = params.row.status === "rejected";

      return (
        <>
          <Tooltip title="View">
            <span>
              <IconButton
                onClick={() => handleView(params.row.poNumber)}
                color="primary"
                size="small"
                >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isClosed ? "Closed PO cannot be modified" : "Edit"}>
            <span>
              <IconButton
                onClick={() => handleEdit(params.row)}
                color="info"
                size="small"
                disabled={isClosed}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <span>
              <IconButton
                onClick={() => handleStatusChange(params.row.poId, "close")}
                color="success"
                size="small"
                disabled={isClosed || isCancel || isReject }
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reject">
            <span>
              <IconButton
                onClick={() => handleStatusChange(params.row.poId, "rejected")}
                color="error"
                size="small"
                disabled={isClosed || isCancel || isReject}
              >
                <HighlightOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Cancel">
            <span>
              <IconButton
                onClick={() => handleStatusChange(params.row.poId, "cancelled")}
                color="warning"
                size="small"
                disabled={isClosed || isCancel || isReject}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      );
    },
  },
    { field: "poNumber", headerName: "PO Number", width: 150 },
    { field: "vendorName", headerName: "Vendor", width: 200 },
    {
  field: "poDate",
  headerName: "PO Date",
  width: 150,
  valueFormatter: (value) => {
    if (!value) return "";

    const date = new Date(value);
    return date.toLocaleDateString("en-GB");
  },
},
    { field: "status", headerName: "Status", width: 130 },
    {
      field: "grandTotal",
      headerName: "Grand Total (₹)",
      width: 150,
      valueFormatter: (value) =>
        `${Math.round(Number(value)).toLocaleString("en-IN")}`,
    },
  ];

  return (
    <>
      <div className="page-location">
        <span>Purchase &gt; View Purchase Orders</span>
      </div>
      <Container maxWidth="lg" sx={{ padding: 1 }}>
        <div style={{ height: 600, width: "100%" }}>
          <div style={{ position: "relative", width: "50%", marginBottom: "1rem" }}>
            <input
              type="text"
              placeholder="🔍 Search by PO number, vendor name, or status..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: "100%",
                padding: "0.75rem 2.5rem 0.75rem 1rem",
                borderRadius: "25px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                outline: "none",
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: "1rem",
                transform: "translateY(-50%)",
                color: "#999",
                pointerEvents: "none",
              }}
            >
              🔍
            </span>
          </div>

          <DataGrid
            rows={filteredPOs}
            columns={columns}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            getRowId={(row) => row.poId}
            sx={{
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f0f8ff",
              },
            }}
          />
        </div>
      </Container>

      <Box
        sx={{
          position: "relative",
          left: 0,
          bottom: 0,
          width: "100%",
          mt: "40px",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

export default ViewPurchaseOrders;
